import { Check, Palette } from "@mui/icons-material";
import { Box, IconButton, ListItemText, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { useState, type MouseEvent } from "react";
import { useAppTheme } from "./AppThemeContext";
import { palettes } from "./palette";

const Swatch = ({ themeId }: { themeId: keyof typeof palettes }) => {
  const palette = palettes[themeId];
  return (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        overflow: "hidden",
        display: "flex",
        flexShrink: 0,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box sx={{ flex: 1, backgroundColor: palette.surface.base }} />
      <Box sx={{ flex: 1, backgroundColor: palette.colors.orange.base }} />
      <Box sx={{ flex: 1, backgroundColor: palette.surface.page }} />
    </Box>
  );
};

export const ThemePicker = () => {
  const { themeId, themes, setThemeId, spec } = useAppTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const close = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title={`Theme: ${spec.name}`}>
        <IconButton onClick={open} size="small" aria-label="Change theme" sx={{ color: "text.primary" }}>
          <Palette />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 2147483647 }}
        slotProps={{ paper: { sx: { minWidth: 232, py: 0.5 } } }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 1, display: "block", color: "text.secondary", fontWeight: 700 }}>
          Appearance
        </Typography>

        {themes.map((entry) => (
          <MenuItem
            key={entry.id}
            selected={entry.id === themeId}
            onClick={() => {
              setThemeId(entry.id);
              close();
            }}
            sx={{ gap: 1.5, py: 1 }}
          >
            <Swatch themeId={entry.id} />
            <ListItemText
              primary={entry.name}
              secondary={entry.mode === "dark" ? "Dark" : "Light"}
              primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
              secondaryTypographyProps={{ fontSize: 12 }}
            />
            {entry.id === themeId && <Check fontSize="small" sx={{ color: "primary.main" }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
